'use strict';

// Libraries
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';

// Services
import UserManager from 'ck-core-client/source/managers/UserManager';
import langToReactElements from 'ck-core-client/source/functions/langToReactElements';
import LANG from './AccountPageLang.js';

// Components
import SideMenu from 'ck-core-client/source/ui/SideMenu';
import UserCards from '../../ui/newPayments/UserCards';
import CurrentPaymentPlan from '../../ui/newPayments/CurrentPaymentPlan';
import AccountDetails from '../../ui/account/AccountDetails';
import CurrencyPropType from '../../ui/newPayments/CurrencyPropType';
import RestrictBillingFooter from '../../ui/newPayments/RestrictBillingFooter';
import BillingRestrictedPanel from '../../ui/newPayments/BillingRestrictedPanel';
import RoundButton from 'ck-core-client/source/ui/RoundButton';
import Help from '../../ui/newPayments/Help';
import ModalsOverlay from 'ck-core-client/source/ui/ModalsOverlay';
import ProgressComponent from 'ck-core-client/source/components/ProgressComponent/index';
import Section from 'ck-core-client/source/components/Layout/Section';
import PageHeader from 'ck-core-client/source/components/Layout/PageHeader';
import { UpgradePlanBanner } from 'ck-website/source/components/upgrades/UpgradePlanBanner';

export const CHOICES = {
	FAMILY: {
		displayName: 'Your Accounts',
		name: 'family'
	},
	PLAN: {
		name: 'plan'
	},
	DETAILS: {
		name: 'details'
	},
	GIFT: {
		displayName: 'Send Gift',
		name: 'gifts'
	},
	HELP: {
		name: 'help'
	},
	PROGRESS: {
		name: 'progress'
	}
};

export default class AccountPageInner extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			selectedPanel: this.props.selectedPanel
		};
	}

	render() {
		const choices = _.values(CHOICES);

		let panel;

		const checkBillingLinked = () => {
			if (this.props.billingLink) {
				return (
					<div className="billingLinked">
						<p>
							You are currently logged in as a coding user. To access your account slots you will
							need to switch to the billing account.
						</p>
						<RoundButton
							handleClick={() =>
								(document.location.href =
									this.props.billingLink + `&panel=${this.state.selectedPanel}`)
							}
							text="Switch to billing user"
						/>

						<RoundButton
							handleClick={() => (document.location.href = '/platform-select/')}
							colour="yellow"
							text="Start coding"
						/>
					</div>
				);
			}

			return false;
		};

		const billingPagePanel = checkBillingLinked();
		let isBillingPage = false;

		if (this.state.selectedPanel === CHOICES.FAMILY.name) {
			isBillingPage = true;
			panel = billingPagePanel || (
				<UserCards
					{...this.props.subUsers}
					handleAddModal={modal => {
						if (this.modalsOverlay) {
							this.modalsOverlay.addModal(modal);
						}
					}}
					handleCloseModal={modal => {
						if (this.modalsOverlay) {
							this.modalsOverlay.closeModal(modal);
						}
					}}
					isBillingOnlyUser={this.props.isBillingOnlyUser}
				/>
			);
		} else if (this.state.selectedPanel === CHOICES.PLAN.name) {
			isBillingPage = true;
			panel = billingPagePanel || (
				<React.Fragment>
					<CurrentPaymentPlan
						planInfo={this.props.planInfo}
						billingMethod={this.props.billingMethod}
						allowCurrencyChange={this.props.allowCurrencyChange}
						currency={this.props.currency}
					/>
					{this.props.planInfo.currentPlan && <UpgradePlanBanner isCompact={true} />}
				</React.Fragment>
			);
		} else if (this.state.selectedPanel === CHOICES.DETAILS.name) {
			panel = (
				<AccountDetails
					{...this.props.accountDetails}
					isBillingOnlyUser={this.props.isBillingOnlyUser}
				/>
			);
		} else if (this.state.selectedPanel === CHOICES.GIFT.name) {
			const href =
				document.location.pathname +
				CK.fn.createQueryString({
					panel: CHOICES.FAMILY.name
				});

			const accountsLink = (
				<a
					href={href}
					onClick={event => {
						this.setState({ selectedPanel: CHOICES.FAMILY.name });
						event.preventDefault();
					}}
				>
					{CHOICES.FAMILY.displayName}
				</a>
			);

			const questions = [
				{
					question: '1. an online link',
					answer: (
						<p>
							Head to {accountsLink} and click <b>Copy register link</b>. Send that link to the
							person you wish to gift the membership to and they can register their gift. The
							membership will begin when registration is complete!
						</p>
					)
				},
				{
					question: '2. a printable gift certificate',
					answer: (
						<p>
							Head to {accountsLink} and click <b>Gift PDF</b> to download it. Print and send (or
							email) the gift certificate to the person you wish to gift the membership to.
							Instructions are included for redeeming their gift. The membership will begin once
							they have completed registration!
						</p>
					)
				}
			];

			panel = <Help title="I want to send my gift as..." questions={questions} />;
		} else if (this.state.selectedPanel === CHOICES.HELP.name) {
			const questions = [
				{
					question: 'How do I start coding?',
					answer: (
						<p>
							That really depends on the account type you have. When you purchased your membership,
							an email was sent to the cardholder email address or PayPal email address. This
							contains a special link that allows you to login and manage your accounts.
							<br />
							<br />
							If you have not yet used Code Kingdoms, you’ll need to register an account. To do this
							click “Your Accounts” then “Register user”. On this page you can set the login
							credentials for your child. Let your child complete this page, or set a username and
							password for them to use.
							<br />
							<br />
							If you already have a Code Kingdoms username and were logged into this account when
							you purchased, then your account has already been upgraded to a paid membership. If
							this was not your intention, you can remove this username from your account and
							register a different one.
							<br />
							<br />
							If you already have a Code Kingdoms username, but were not logged into the account
							when you purchased, you’ll need to invite an existing user to join your paid
							membership. To do this select “Your accounts”, click “Invite an existing user” in one
							of your open slots and enter the username you wish to invite. After this, that user
							will have full access to a paid membership.
							<br />
							<br />
							Once a user has been assigned to an active membership, you can access the full Code
							Editor via the “Start coding” link in the top menu bar.
						</p>
					)
				},
				{
					question: 'How do I cancel my membership?',
					answer: (
						<p>
							If you have a subscription membership, with a recurring charge, you can cancel at any
							time simply by going to your account page when logged in as the billing customer.
							<br />
							<br />
							{`Select the "Plan" panel, click the 'Cancel subscription' button and follow the steps. If you decide to reactivate you can do so on the same page.`}
							<br />
							<br />
							If you purchased a 12 month or lifetime membership and are still within your 30-day
							refund window, please contact customer support to arrange your refund.
						</p>
					)
				},

				{
					question: 'Can I speak to someone on your team?',
					answer: (
						<p>
							Of course, we can be reached by our chat system (click the icon in the bottom right of
							the screen), or if you prefer, you can email us at{' '}
							{CK.config.name === 'ck-coolmath'
								? '<a href="mailto:help@coolmathcoding.com">help@coolmathcoding.com</a>'
								: '<a href="mailto:support@codekingdoms.com">support@codekingdoms.com</a>'}
						</p>
					)
				}
			];

			panel = <Help title="Common account questions" questions={questions} />;
		} else if (this.state.selectedPanel === CHOICES.PROGRESS.name) {
			const tableProps = {
				headings: [],
				loading: false
			};
			panel = this.props.progressData ? (
				<React.Fragment>
					<PageHeader title={LANG.PROGRESS_TITLE} />
					{this.props.subUsers.cards.length !== 0 && <div>{LANG.BILLING_PAGE_PROGRESS}</div>}
					<ProgressComponent
						tableProps={tableProps}
						data={this.props.progressData}
						type="all"
						showTables={true}
					/>
				</React.Fragment>
			) : (
				<Section borderColor="lightGrey">
					<p> {LANG.NO_PROGRESS}</p>
				</Section>
			);
		}

		let logoutButton;

		// Issue with cycling between the billing account & coding account prevents some users from logging out at all
		if (
			true
			// _.includes(this.props.hiddenPanels, CHOICES.FAMILY.name) ||
			// this.props.isBillingRestricted
		) {
			logoutButton = (
				<div className="logout">
					<RoundButton
						text="Logout"
						size="small"
						handleClick={() => UserManager.mod().logout()}
						colour="red"
					/>
				</div>
			);
		}

		return (
			<div className="AccountPage">
				{logoutButton}
				<div className="maxWidthContentWrapper">
					{this.props.message && (
						<div className="message">{langToReactElements(this.props.message)}</div>
					)}
					<SideMenu
						onSelect={choice => this.setState({ selectedPanel: choice.name })}
						choices={_.filter(choices, choice => !_.includes(this.props.hiddenPanels, choice.name))}
						selectedChoice={this.state.selectedPanel}
					/>
					<div
						className={classnames(
							'panel',
							this.state.selectedPanel === CHOICES.PROGRESS.name ? 'panel--noBackground' : ''
						)}
					>
						{(!billingPagePanel && isBillingPage && this.props.isBillingRestricted && (
							<BillingRestrictedPanel />
						)) ||
							panel}
					</div>
					{!billingPagePanel && isBillingPage && this.props.showRestrictBillingFooter && (
						<RestrictBillingFooter hasRestrictedBilling={this.props.hasRestrictedBilling} />
					)}
				</div>
				<ModalsOverlay ref={overlay => (this.modalsOverlay = overlay)} />
			</div>
		);
	}
}

AccountPageInner.defaultProps = {
	selectedPanel: CHOICES.FAMILY.name
};

AccountPageInner.propTypes = {
	progressData: PropTypes.object,
	subUsers: PropTypes.shape(UserCards.propTypes),
	planInfo: CurrentPaymentPlan.propTypes.planInfo,
	accountDetails: PropTypes.shape(AccountDetails.propTypes),
	billingMethod: PropTypes.any,
	allowCurrencyChange: PropTypes.bool,
	isBillingOnlyUser: PropTypes.bool,
	currency: CurrencyPropType,
	message: PropTypes.string,
	showRestrictBillingFooter: PropTypes.bool,
	hasRestrictedBilling: PropTypes.bool,
	isBillingRestricted: PropTypes.bool,
	selectedPanel: PropTypes.oneOf(_.map(CHOICES, 'name')),
	hiddenPanels: PropTypes.arrayOf(PropTypes.string),
	billingLink: PropTypes.string
};
